import { useMemo, useRef, useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import styles from './crm.module.css';
import { cancelEditor, EditorCloseButton } from './crmEditorCancel';

/**
 * Checklist cell editor for enum-family options (E6.4): selected chips + a searchable checkbox list.
 * `multi_enum` commits a string[]; `single` (a long single-enum) commits a string|null and closes on
 * pick. Options come from the field's picklist via cellEditorParams — the editor only offers valid
 * values. Used as a cellEditorPopup. Shared primitive (D10) — the filter builder reuses it later.
 *
 * PR3 item 8: ✕ closes WITHOUT changes, restoring the value the editor opened with — see
 * `crmEditorCancel`, which owns the two hosts' discard mechanisms.
 */
export function CrmChecklistEditor(
  props: CustomCellEditorProps & { options?: string[]; single?: boolean; onCancelEdit?: () => void },
) {
  const { value, onValueChange, stopEditing } = props;
  const options = props.options ?? [];
  const single = !!props.single;
  const initial: string[] = Array.isArray(value)
    ? value.map(String)
    : value == null || value === '' ? [] : [String(value)];
  // the RAW value the editor opened with — what ✕ restores. Not `initial`, which is normalized to an
  // array: a single column must go back to its `string | null`, not to `['Alpha']`.
  const initialRaw = useRef(value);
  const [sel, setSel] = useState<string[]>(initial);
  const [q, setQ] = useState('');

  const commit = (next: string[]) => {
    setSel(next);
    onValueChange(single ? next[0] ?? null : next);
  };
  const toggle = (opt: string) => {
    if (single) { commit([opt]); stopEditing(); return; }
    commit(sel.includes(opt) ? sel.filter((x) => x !== opt) : [...sel, opt]);
  };
  const shown = useMemo(() => {
    // include any currently-selected value NOT in the picklist (an option renamed/removed since it was
    // set) so the operator SEES it (checked) and can keep or clear it — it must not silently vanish
    // during edit. Extras sort after the real options.
    const extras = sel.filter((s) => !options.includes(s));
    const all = extras.length ? [...options, ...extras] : options;
    const n = q.trim().toLowerCase();
    return n ? all.filter((o) => o.toLowerCase().includes(n)) : all;
  }, [options, sel, q]);

  return (
    <div className={styles.refEditor}>
      {!single && (
        <div className={styles.refEditorChips}>
          {sel.length === 0 && <span className={styles.gridEmpty}>none</span>}
          {sel.map((s) => (
            <span key={s} className={`${styles.chip} ${styles.chipMuted}`}>
              {s}
              <button className={styles.refEditorRemove} aria-label={`Remove ${s}`} onClick={() => toggle(s)}>×</button>
            </span>
          ))}
        </div>
      )}
      <input
        className={styles.refEditorSearch}
        autoFocus
        value={q}
        placeholder="Search options…"
        onChange={(e) => setQ(e.target.value)}
      />
      <div className={styles.refEditorResults}>
        {shown.length === 0 && <span className={styles.gridEmpty}>No options.</span>}
        {shown.map((o) => (
          <label key={o} className={styles.checklistRow}>
            <input type="checkbox" checked={sel.includes(o)} onChange={() => toggle(o)} />
            {o}
          </label>
        ))}
      </div>
      <div className={styles.refEditorFooter}>
        <EditorCloseButton onClick={() => cancelEditor(props, initialRaw.current)} />
        {!single && <button className={styles.gridPageBtn} onClick={() => stopEditing()}>Done</button>}
      </div>
    </div>
  );
}
